<template>
  <div
    class="footer-bar"
    data-test="graphics-footer-bar"
  >
    <div class="footer-bar__inside">
      <div class="canvas-controls">
        <div class="canvas-controls__widthHeight">
          <validation-field as="input"
            v-model.number="canvasWidth"
            rules="required|numeric"
            type="number"
            name="canvasWidth"
            class="canvas-controls__input"
            :class="{ 'is-invalid': validationErrors['canvasWidth'] }"
            data-test="graphics-canvasWidth"
            data-entity="graphics-inputCanvasWidth"
            @change="triggerResize"
          />
          x
          <validation-field as="input"
            v-model.number="canvasHeight"
            rules="required|numeric"
            type="number"
            name="canvasHeight"
            class="canvas-controls__input"
            :class="{ 'is-invalid': validationErrors['canvasHeight'] }"
            data-test="graphics-canvasHeight"
            data-entity="graphics-inputCanvasHeight"
            @change="triggerResize"
          />
        </div>
        <div class="canvas-controls__safeArea">
          <label
            class="checkbox"
            data-balloon="⌘; or ctrl ;"
            data-balloon-pos="up-left"
          >Safe area
            <input
              v-model="showSafeZone"
              type="checkbox"
              data-test="graphics-toggleSafeArea"
              data-entity="graphics-toggleSafeArea"
            >
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check" />
            </div>
          </label>

          <div
            v-if="showSafeZone"
            class="sidebar-form-group settings-row"
            data-test="graphics-SafeAreaSettings"
          >
            <div class="settings-col-6">
              <div class="input-group">
                <validation-field as="input"
                  v-model.number="safeAreaWidth"
                  rules="required|numeric"
                  type="number"
                  name="safeAreaWidth"
                  class="canvas-controls__input"
                  :class="{ 'is-invalid': validationErrors['safeAreaWidth'] }"
                  data-test="graphics-safeAreaWidth"
                  data-entity="graphics-inputSafeAreaWidth"
                />
                <span class="input-group-addon symbol">%</span>
              </div>
            <!-- <label class="sidebar-input-sub-label">Width</label> -->
            </div>
            <div class="settings-col-6">
              <div class="input-group">
                <validation-field as="input"
                  v-model.number="safeAreaHeight"
                  rules="required|numeric"
                  type="number"
                  name="safeAreaHeight"
                  class="canvas-controls__input"
                  :class="{ 'is-invalid': validationErrors['safeAreaHeight'] }"
                  data-test="graphics-safeAreaHeight"
                  data-entity="graphics-inputSafeAreaHeight"
                />
                <span class="input-group-addon symbol">%</span>
              </div>
            <!-- <label class="sidebar-input-sub-label">Height</label> -->
            </div>
          </div>
        </div>
        <div class="canvas-controls__rulers">
          <label
            class="checkbox"
            data-balloon="⇧R"
            data-balloon-pos="up-left"
          >Rulers and Guides
            <input
              v-model="showRulers"
              type="checkbox"
              data-test="graphics-toggleRulers"
              data-entity="graphics-toggleRulers"
              @change="toggleRuler"
            >
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check" />
            </div>
          </label>

          <div
            v-if="showRulers"
            data-test="graphics-GuidesSettings"
          >
            <button
              class="btn btn-sm btn-style-b canvas-controls__clearGuides"
              :disabled="clearGuidesDisabled"
              data-test="graphics-clearGuides"
              data-entity="graphics-clearGuides"
              @click="clearRulerGuides"
            >
              <span
                class="fas fa-ruler-combined"
                style="transform: rotate(90deg)"
              />
              Clear Guides
            </button>
          </div>
        </div>
      </div>

      <div
        class="zoom-controls"
        role="group"
        aria-label="Zoom Controls"
      >
        <div class="zoom-group">
          <button
            data-balloon="Selection (V)"
            data-balloon-pos="up"
            data-test="graphics-selectSelectionMode"
            data-entity="graphics-selectSelectionMode"
            class="btn btn-sm btn-icon btn-style-b zoom-group__modeButton"
            :class="{active: !panMode}"
            @click="panMode = false"
          >
            <span class="fas fa-mouse-pointer"></span>
            <span class="sr-only">Selection Tool</span>
          </button>
          <button
            data-balloon="Hand (H)"
            data-balloon-pos="up"
            data-test="graphics-selectPanMode"
            data-entity="graphics-selectPanMode"
            class="btn btn-sm btn-icon btn-style-b zoom-group__modeButton"
            :class="{active: panMode}"
            @click="panMode = true"
          >
            <span class="fas fa-hand-paper"></span>
            <span class="sr-only">Canvas Pan Tool</span>
          </button>
        </div>
        <div class="zoom-group">
          <label
            class="range-slider__label sr-only"
            for="zoom"
          >Zoom</label>
          <div class="zoom-group__controls">
            <div
              class="range-slider"
              data-entity="graphics-rangeSliderZoomContainer"
              data-balloon="Slide to zoom canvas."
              data-balloon-pos="up"
            >
              <input
                v-model.number="scaleFactorSelectedRounded"
                data-test="graphics-slideZoom"
                data-entity="graphics-slideZoom"
                class="range-slider__input no-tooltip"
                type="range"
                name="zoomSlider"
                :min="minZoom"
                :max="maxZoom"
                aria-label="Alternative slider input for zoom"
                @focus="scaleFactorMode = 1"
              />
            </div>
            <!-- yay more bootstrap :(,
                but it's the UI we have and I don't want another one-off design -->
            <div
              class="dropup"
              data-balloon="Zoom (cmd +/-)"
              data-balloon-pos="up"
            >
              <button
                type="button"
                class="btn btn-style-b btn-sm dropdown-toggle"
                data-toggle="dropdown"
                data-test="graphics-toggleZoomDropdown"
                data-entity="graphics-toggleZoomDropdown"
              >
                <span class="btn-label">{{ scaleFactorSelectedRounded }} </span>
                <span class="">%</span>
                <span class="sr-only">Toggle Zoom Dropdown</span>
              </button>
              <ul
                class="dropdown-menu pull-right"
                role="menu"
              >
                <li
                  v-for="factor in scaleFactors"
                  :key="factor"
                  class="dropdown-menu-item"
                >
                  <button
                    class="zoom-group__dropup--options"
                    :data-test="`graphics-selectZoomFactor-${factor}`"
                    :data-entity="`graphics-selectZoomFactor-${factor}`"
                    @click="scaleFactorMode = 1, scaleFactorSelected = factor"
                  >
                    {{ factor }}%
                  </button>
                </li>
                <li class="divider" />
                <li class="dropdown-menu-item">
                  <button
                    type="button"
                    class="zoom-group__dropup--options"
                    data-test="graphics-selectZoomFit"
                    data-entity="graphics-selectZoomFit"
                    @click="onFitClick"
                  >
                    Fit to view
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import EventBus from '../utils/event-bus';

export default {
  emits: ['fit'],
  name: 'TemplateFooter',


  computed: {
    ...mapGetters('panAndZoom', {
      scaleFactor: 'getScaleFactor',
      scaleFactors: 'getScaleFactors',
      panModeState: 'getPanMode',
      minZoom: 'getMinZoom',
      maxZoom: 'getMaxZoom',
      scaleFactorModeState: 'getScaleFactorMode',
      scaleFactorSelectedState: 'getScaleFactorSelected',
      scaleFactorFitState: 'getScaleFactorFit',
    }),

    ...mapGetters('graphicsTemplate', {
      canvas: 'getCanvas',
      guides: 'getGuides',
      safeArea: 'getSafeArea',
    }),

    canvasWidth: {
      get() {
        return this.canvas.width;
      },
      set(value) {
        this.setCanvasWidth({
          value,
        });
      },
    },

    canvasHeight: {
      get() {
        return this.canvas.height;
      },
      set(value) {
        this.setCanvasHeight({
          value,
        });
      },
    },

    showSafeZone: {
      get() {
        return this.safeArea.show;
      },
      set(value) {
        this.setSafeAreaShow({
          value,
        });
      },
    },

    safeAreaWidth: {
      get() {
        return this.safeArea.width;
      },
      set(value) {
        this.setSafeAreaWidth({
          value,
        });
      },
    },

    safeAreaHeight: {
      get() {
        return this.safeArea.height;
      },
      set(value) {
        this.setSafeAreaHeight({
          value,
        });
      },
    },

    showRulers: {
      get() {
        return this.guides.show;
      },
      set(value) {
        this.setGuidesShow({
          value,
        });
      },
    },

    panMode: {
      get() {
        return this.panModeState;
      },
      set(v) {
        this.setPanMode(v);
      },
    },

    scaleFactorMode: {
      get() {
        return this.scaleFactorModeState;
      },
      set(v) {
        this.setScaleFactorMode(v);
      },
    },

    scaleFactorSelected: {
      get() {
        return this.scaleFactorSelectedState;
      },
      set(v) {
        this.setScaleFactorSelected(v);
      },
    },

    scaleFactorFit: {
      get() {
        return this.scaleFactorFitState;
      },
      set(v) {
        this.setScaleFactorFit(v);
      },
    },

    scaleFactorSelectedRounded: {
      get() {
        return Math.round(this.scaleFactorSelected);
      },
      set(v) {
        this.scaleFactorSelected = v;
      },
    },

    clearGuidesDisabled() {
      return (
        this.guides.vGuides.length === 0
        && this.guides.hGuides.length === 0
      );
    },
  },

  mounted() {
    // using documentElement so we can detect if we are in a text editable area
    // because there we need shift R to do normal casing things
    document.documentElement.addEventListener(
      'keydown',
      this.keyListener,
    );
  },
  beforeUnmount() {
    document.documentElement.removeEventListener('keydown', this.keyListener);
  },

  methods: {
    ...mapActions('panAndZoom', [
      'setScaleFactor',
      'setPanMode',
      'setScaleFactorMode',
      'setScaleFactorSelected',
      'setScaleFactorFit',
    ]),

    ...mapActions('graphicsTemplate', [
      'setCanvasWidth',
      'setCanvasHeight',
      'setGuidesShow',
      'setSafeAreaShow',
      'setSafeAreaWidth',
      'setSafeAreaHeight',
      'clearGuides',
    ]),

    onFitClick() {
      this.scaleFactorMode = 0;
      this.scaleFactorSelected = this.scaleFactorFit * 100;
      this.$emit('fit');
    },

    triggerResize() {
      $(window).trigger('resize');
    },

    toggleRuler() {
      EventBus.emit('rulers-load-guides');
    },
    clearRulerGuides() {
      this.clearGuides();
      EventBus.emit('rulers-load-guides');
    },

    keyListener(e) {
      const isRulerHotkey = e.key === 'R' && e.shiftKey;
      const isTypingElement = (element) => (
        element instanceof HTMLTextAreaElement
          || (
            element instanceof HTMLInputElement
            && (
              !element.type
              || element.type === 'text'
              || element.type === 'search'
            )
          )
        || (element instanceof HTMLDivElement && element.contentEditable)
      );

      if (!isRulerHotkey) return;

      if (isTypingElement(e.target)) {
        return;
      }
      e.preventDefault();
      this.showRulers = !this.showRulers;
      this.toggleRuler();
    },
  },
};
</script>

<style lang="scss" scoped>
.footer-bar {
  position: fixed;
  height: 40px;
  box-sizing: border-box;
  bottom: 0;
  z-index: 1;
  width: 100%;
  left: 0;
  right: 0;
  margin: auto;
  min-width: 840px; // smallest the rest of the ui supports
  border: 1px solid #dfeaef;
  border-left: none;
  border-right: none;
  background: #fff;
  align-items: center;
  justify-content: center;
  display: flex;

  &__inside {
    display: flex;
    justify-content: space-between;
    width: 72%;
  }

  .canvas-controls {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 0 20px;
    white-space: nowrap;

    &__widthHeight {
      border-right: 1px solid #dfeaef;
      padding-right: 10px;
    }

    &__input {
      max-width: 6em;
      padding: 5px;
    }

    &__safeArea {
      display: flex;
      align-items: center;
      border-right: 1px solid #dfeaef;
      padding-right: 10px;
      height: 30px;

      label{
        margin-right: 10px
      }
    }

    &__rulers {
      display: flex;
      align-items: center;

      label{
        margin-right: 10px;
      }
    }

    &__clearGuides {
      font-size: 12px;
    }

    .input-group-addon{
      padding: 5px;
    }
  }

  .zoom-controls {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    height: 100%;
    padding: 0 20px;
    align-items: center;

    .zoom-group {
      display: flex;
      align-items: center;

      &__controls {
        display: flex;
        align-items: center;
      }
      .dropdown-menu {
        min-width: auto;
        white-space: nowrap;
        padding-bottom: 8px;
        padding-top: 4px;
      }

      .dropdown-toggle {
        width: 60px;
        text-align: right;
      }

      &__modeButton {
        width: 30px;
         &.active {
          background: #2bacca;
          border-color: #2bacca;
          color: #ffffff;
         }
      }

      &__dropup {
        &--options {

          padding: 4px 16px;
          border: 0;
          background: inherit;
          width: 100%;
          text-align: left;
        }
      }

      &:not(:first-child) {
        border-left: 1px solid #dfeaef;
        height: 30px;
      }
    }

    .range-slider {
      width: 300px;

      &__input {
        margin: auto;
        position: static;
      }
    }

    .range-slider__label {
      margin: 0;
    }
  }
}
</style>
